import type { JSX } from "react";

import type { AuthBlockReason } from "@/auth/authState";
import ErrorCodes from "@/constants/errors/ErrorCodes";

interface Copy {
  title: string;
  message: string;
}

// Reason -> user copy. Principal cases map to the backend AUTH-E entries so the
// wording stays in one place; config/token failures get a plain fallback.
const copyFor = (reason?: AuthBlockReason): Copy => {
  const fromCode = (code: keyof typeof ErrorCodes): Copy => {
    const e = ErrorCodes[code];
    return { title: e.title ?? "Something went wrong", message: e.message };
  };
  switch (reason) {
    case "PRINCIPAL_UNRESOLVED":
      return fromCode("AUTH-E-0010");
    case "PRINCIPAL_INACTIVE":
      return fromCode("AUTH-E-0011");
    case "TOKEN_REJECTED":
      // Not "sign in again": the token was rejected as invalid or wrong-audience,
      // which a re-login cannot fix, which is why this is a terminal state.
      return {
        title: "We could not verify your sign in",
        message:
          "There is a problem with your sign in token. Please contact your administrator.",
      };
    case "CONFIG_INVALID":
    default:
      return {
        title: "We could not sign you in",
        message:
          "There is a problem with the sign in configuration. Please contact your administrator.",
      };
  }
};

/**
 * Terminal auth state screen. Shown for `blocked`/`failed` phases, which a
 * re-login cannot fix (no account, deactivated account, or a config/token
 * problem). Rendered by App ahead of the data splash so a blocked principal
 * gets a message instead of an endless spinner.
 */
const AuthBlockedScreen = ({
  reason,
}: {
  reason?: AuthBlockReason;
}): JSX.Element => {
  const { title, message } = copyFor(reason);
  return (
    <div className="flex h-lvh flex-col items-center justify-center gap-4 bg-[#f5f7fa] px-6 text-center">
      <img
        className="w-[220px]"
        src="/images/logo/harris_questbrand_complete_logo.png"
        alt="Harris QuestBrand"
      />
      <h1 className="text-xl font-semibold text-text-500">{title}</h1>
      <p className="max-w-md whitespace-pre-line text-text-400">{message}</p>
    </div>
  );
};

export default AuthBlockedScreen;
